import React, { useId } from "react";
import StarFull from "@/components/icons/starFull";
import StarEmpty from "@/components/icons/starEmpty";
import styles from "./index.module.scss";

const DEFAULT_MAX = 5;

export interface PollStarProps {
  /** Current rating (0 to maxStars) */
  value: number;
  /** Called when user selects a rating */
  onChange: (value: number) => void;
  /** Number of stars (default 5) */
  maxStars?: number;
  /** Disable interaction */
  disabled?: boolean;
  /** Star size in pixels (default 24) */
  size?: number;
  /** Optional className for the root container */
  className?: string;
}

export const PollStar: React.FC<PollStarProps> = ({
  value,
  onChange,
  maxStars = DEFAULT_MAX,
  disabled = false,
  size = 24,
  className,
}) => {
  const handleClick = (rating: number) => {
    if (disabled) return;
    onChange(rating);
  };

  const handleKeyDown = (e: React.KeyboardEvent, rating: number) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(rating);
    }
  };

  const baseId = useId();

  return (
    <div
      className={`${styles.root} ${disabled ? styles.root_disabled : ""} ${className ?? ""}`}
      role="group"
      aria-label={`Rating: ${value} out of ${maxStars}`}
    >
      {Array.from({ length: maxStars }, (_, i) => {
        const starValue = i + 1;
        const isFilled = starValue <= value;
        const clipPathId = `${baseId}-${i}`;
        return (
          <button
            key={i}
            type="button"
            className={styles.star}
            onClick={() => handleClick(starValue)}
            onKeyDown={(e) => handleKeyDown(e, starValue)}
            disabled={disabled}
            aria-label={`${starValue} star${starValue !== 1 ? "s" : ""}`}
            aria-pressed={value === starValue}
          >
            {isFilled ? (
              <StarFull width={size} height={size} clipPathId={clipPathId} />
            ) : (
              <StarEmpty width={size} height={size} clipPathId={clipPathId} />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PollStar;
